<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import MemberHeader from "@/components/member/MemberHeader.vue";
import GovernmentSidebar from "@/components/government/GovernmentSidebar.vue";
import UserManagement from "@/components/government/administration/UserManagement.vue";
import SectorManagement from "@/components/government/administration/SectorManagement.vue";
import NdcAllocation from "@/components/government/administration/NdcAllocation.vue";
import BufferAccounts from "@/components/government/administration/BufferAccounts.vue";
import TransactionAccounts from "@/components/government/administration/TransactionAccounts.vue";

const { t } = useI18n();

const activeTab = ref('user-management');

const setActiveTab = (tab: string) => {
  activeTab.value = tab;
};
</script>

<template>
  <MemberHeader />

  <div class="flex h-screen bg-gray-50">
    <GovernmentSidebar />
    <div id="main-content" class="flex-1 flex flex-col">
      <!-- Header -->
      <header id="header" class="bg-white border-b border-light-gray p-5">
        <div class="flex justify-between items-center">
          <div>
            <h2 class="text-2xl font-medium text-dark-navy">{{ $t('government.administration.title') }}</h2>
            <p class="text-gray-600">{{ $t('government.administration.subtitle') }}</p>
          </div>
          <div class="flex items-center space-x-4">
            <button
              class="p-2 rounded-md border border-light-gray hover:bg-gray-50"
            >
              <i class="fa-solid fa-bell text-gray-600"></i>
            </button>
            <div class="flex items-center space-x-3">
              <img
                src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-2.jpg"
                alt="Admin"
                class="w-8 h-8 rounded-full"
              />
              <span class="text-sm font-medium">{{ $t('government.dashboard_page.admin_user') }}</span>
            </div>
          </div>
        </div>
      </header>

      <!-- Administration Content -->
      <main id="admin-main" class="flex-1 p-5 overflow-y-auto">
        <!-- Admin Navigation Tabs -->
        <div id="admin-tabs" class="border-b border-light-gray mb-6">
          <nav class="flex space-x-8">
            <button
              @click="setActiveTab('user-management')"
              :class="[
                'py-2 px-1 border-b-2 font-medium',
                activeTab === 'user-management'
                  ? 'border-forest-green text-forest-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              ]"
            >
              {{ $t('government.administration.tabs.user_management') }}
            </button>
            <button
              @click="setActiveTab('sector-management')"
              :class="[
                'py-2 px-1 border-b-2 font-medium',
                activeTab === 'sector-management'
                  ? 'border-forest-green text-forest-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              ]"
            >
              {{ $t('government.administration.tabs.sector_management') }}
            </button>
            <button
              @click="setActiveTab('ndc-allocation')"
              :class="[
                'py-2 px-1 border-b-2 font-medium',
                activeTab === 'ndc-allocation'
                  ? 'border-forest-green text-forest-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              ]"
            >
              {{ $t('government.administration.tabs.ndc_allocation') }}
            </button>
            <button
              @click="setActiveTab('buffer-accounts')"
              :class="[
                'py-2 px-1 border-b-2 font-medium',
                activeTab === 'buffer-accounts'
                  ? 'border-forest-green text-forest-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              ]"
            >
              {{ $t('government.administration.tabs.buffer_accounts') }}
            </button>
            <button
              @click="setActiveTab('transaction-accounts')"
              :class="[
                'py-2 px-1 border-b-2 font-medium',
                activeTab === 'transaction-accounts'
                  ? 'border-forest-green text-forest-green'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              ]"
            >
              {{ $t('government.administration.tabs.transaction_accounts') }}
            </button>
          </nav>
        </div>

        <!-- Tab Content -->
        <div class="tab-content">
          <UserManagement v-if="activeTab === 'user-management'" />
          <SectorManagement v-if="activeTab === 'sector-management'" />
          <NdcAllocation v-if="activeTab === 'ndc-allocation'" />
          <BufferAccounts v-if="activeTab === 'buffer-accounts'" />
          <TransactionAccounts v-if="activeTab === 'transaction-accounts'" />
        </div>
      </main>
    </div>
  </div>
</template>
